'use client';

import React from 'react';
import type { AircraftType, Airstrip, Customer, AdminSettings, Leg, ResolvedExtraService, Role, UserAccount } from '@/types';
import type { Advisory } from '../../services/advisoryRules';
import QuoteControls from './QuoteControls';
import QuoteLegs from './QuoteLegs';
import QuoteSummary from './QuoteSummary';
import { X } from 'lucide-react';

type QuoteEditorProps = {
  aircrafts: AircraftType[];
  airstrips: Airstrip[];
  customers: Customer[];
  settings: AdminSettings;
  currentRole: Role;
  currentUser?: UserAccount | null;
  formData: {
    customerId: string;
    aircraftModel: string;
    date: string;
    issuerDiscount: number;
    ceoDiscount: number;
    pricingMode: 'HOURS' | 'MILEAGE';
    waitingMinutes: number;
    sleepOverEnabled: boolean;
    sleepOverOption: 'NONE' | 'TIER1' | 'TIER2' | 'CUSTOM';
    sleepOverCustomAmount: number;
    bookingId?: string;
    pnrCode?: string;
    legs: Leg[];
  };
  weatherByCode: Record<string, { condition?: string; wind?: string } | null>;
  totals: {
    base: number;
    landing: number;
    handling: number;
    nav: number;
    taxes: number;
    safetySecurity: number;
    totalPax?: number;
    perPaxDepartureTax?: number;
    perPaxSafetySecurity?: number;
    regulatoryByLeg?: Array<{
      id: string;
      from: string;
      to: string;
      pax: number;
      departureTaxTotal: number;
      safetySecurityTotal: number;
    }>;
    legBreakdowns?: Array<{
      id: string;
      from: string;
      to: string;
      isDeadLeg?: boolean;
      pax: number;
      distanceMiles: number;
      blockMinutes: number;
      baseCharge: number;
      landing: number;
      handling: number;
      navigation: number;
      departureTaxes: number;
      aircraftTax: number;
      aircraftTaxComponents: {
        landing: number;
        handling: number;
        navigation: number;
        paxBusLuggage: number;
        aircraftPermit: number;
        clearance: number;
        reposition: number;
      };
      safetySecurity: number;
      perPaxDepartureTax: number;
      perPaxSafetySecurity: number;
      ancillaries: number;
      extraServices: number;
      total: number;
    }>;
    maFees: number;
    loungeFees: number;
    ancillaryLines: Array<{
      legId: string;
      legNumber: number;
      from: string;
      to: string;
      name: string;
      scope: 'ORIGIN' | 'DESTINATION';
      amount: number;
    }>;
    extraServices: number;
    extraServiceLines: Array<{
      legId: string;
      legNumber: number;
      from: string;
      to: string;
      code: string;
      name: string;
      scope: string;
      amount: number;
    }>;
    waiting: number;
    discountVal: number;
    discountBreakdown: {
      clientPercent: number;
      issuerPercent: number;
      ceoPercent: number;
      totalPercent: number;
      clientAmount: number;
      issuerAmount: number;
      ceoAmount: number;
    };
    total: number;
    margin: number;
  };
  advisories: Advisory[];
  onClose: () => void;
  onUpdateForm: (updates: Partial<QuoteEditorProps['formData']>) => void;
  onResetDiscountSource: () => void;
  onIssuerDiscountChange: (value: number) => void;
  onCeoDiscountChange: (value: number) => void;
  onAddLeg: () => void;
  onAutoReturn: () => void;
  onRemoveLeg: (id: string) => void;
  onUpdateLeg: (id: string, updates: Partial<Leg>) => void;
  resolvedExtraServicesByLeg: Record<string, ResolvedExtraService[]>;
  onSave: () => void;
  canAuthorizeQuote: boolean;
  authorizeBlockReason?: string;
  includeBreakdownDefault: boolean;
  onToggleIncludeBreakdown: (value: boolean) => void;
  sleepOverAssessment: {
    autoEligible: boolean;
    reason: string;
  };
};

const QuoteEditor: React.FC<QuoteEditorProps> = ({
  aircrafts,
  airstrips,
  customers,
  settings,
  currentRole,
  currentUser,
  formData,
  weatherByCode,
  totals,
  advisories,
  onClose,
  onUpdateForm,
  onResetDiscountSource,
  onIssuerDiscountChange,
  onCeoDiscountChange,
  onAddLeg,
  onAutoReturn,
  onRemoveLeg,
  onUpdateLeg,
  resolvedExtraServicesByLeg,
  onSave,
  canAuthorizeQuote,
  authorizeBlockReason,
  includeBreakdownDefault,
  onToggleIncludeBreakdown,
  sleepOverAssessment,
}) => {
  const discountLimit = Number(currentUser?.charterDiscountCap ?? settings.discountCapsByRole[currentRole] ?? 0);
  const charterGuidanceNotes = String(
    settings.agent?.knowledgeBaseNotesByModule?.QUOTER ||
    settings.agent?.guidance?.knowledgeBaseNotes ||
    ''
  ).trim();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-10 items-start">
        {/* Main Editor Panel */}
        <div className="bg-gradient-to-br from-white via-slate-50/50 to-white rounded-3xl border border-slate-200/60 shadow-xl overflow-visible w-full hover:shadow-2xl transition-shadow">
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-7 py-6 border-b border-slate-200/70 bg-gradient-to-r from-slate-900/5 via-indigo-900/5 to-slate-900/5">
            <div>
              <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-[0.15em] mb-1">✈ Mission Composer</p>
              <h3 className="text-3xl font-bold text-slate-900">Charter Mission Builder</h3>
              <p className="text-sm text-slate-600 mt-1">Build quotes fast using registry defaults and route overrides.</p>
            </div>
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
              <span className="badge badge-compact bg-indigo-100/80 text-indigo-700 border border-indigo-200">
                Booking: {formData.bookingId || 'Pending'}
              </span>
              <span className="badge badge-compact bg-teal-100/80 text-teal-700 border border-teal-200">
                PNR: {formData.pnrCode || 'Pending'}
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 text-slate-500 hover:text-red-600 hover:bg-red-50/80 transition-all border border-transparent hover:border-red-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-7 space-y-7">
            <QuoteControls
              customerId={formData.customerId}
              aircraftModel={formData.aircraftModel}
              date={formData.date}
              pricingMode={formData.pricingMode}
              waitingMinutes={formData.waitingMinutes}
              sleepOverEnabled={formData.sleepOverEnabled}
              sleepOverOption={formData.sleepOverOption}
              sleepOverCustomAmount={formData.sleepOverCustomAmount}
              customers={customers}
              aircrafts={aircrafts}
              settings={settings}
              advisories={advisories}
              guidanceNotes={charterGuidanceNotes}
              sleepOverAssessment={sleepOverAssessment}
              onChange={onUpdateForm}
              onResetDiscountSource={onResetDiscountSource}
            />

            <QuoteLegs
              legs={formData.legs}
              airstrips={airstrips}
              weatherByCode={weatherByCode}
              issueDate={formData.date}
              advisories={advisories}
              sleepOverChargeByLeg={Object.fromEntries((totals.legBreakdowns || []).map(item => [item.id, Number(item.sleepOverCharge || 0)]))}
              onAddLeg={onAddLeg}
              onAutoReturn={onAutoReturn}
              onRemoveLeg={onRemoveLeg}
              onUpdateLeg={onUpdateLeg}
              resolvedExtraServicesByLeg={resolvedExtraServicesByLeg}
            />
          </div>
        </div>

        {/* Summary Sidebar */}
        <QuoteSummary
          totals={totals}
          issuerDiscount={formData.issuerDiscount}
          ceoDiscount={formData.ceoDiscount}
          issuerDiscountLimit={discountLimit}
          onIssuerDiscountChange={onIssuerDiscountChange}
          onCeoDiscountChange={onCeoDiscountChange}
          canEditCeoDiscount={currentRole === 'CEO'}
          onSave={onSave}
          canAuthorizeQuote={canAuthorizeQuote}
          authorizeBlockReason={authorizeBlockReason}
          includeBreakdownDefault={includeBreakdownDefault}
          onToggleIncludeBreakdown={onToggleIncludeBreakdown}
          advisories={advisories}
          guidanceNotes={charterGuidanceNotes}
        />
      </div>
    </div>
  );
};

export default QuoteEditor;
